import React, { useContext, useEffect, useState } from 'react'
import { ScrollView, StyleSheet, Text, View } from 'react-native'
import { TemplatePage } from '../../../../../components/TemplatePage'
import { globalStyle } from '../../../../../theme/global'
import { GrayHeader } from '../../components/GrayHeader'
import { Header } from './components/Header'
import { Separator } from '../../../../../components/Separator'
import { Button } from '../../../../../components/Button'
import { FuelContext } from '../../../../../context/Logistics/fuel/FuelProvider'
import { Card } from './components/Card'
import { AlertMessage } from '../../../../../components/AlertMessage'
import { SuccessIcon } from '../../../../../components/Svg'
import { corporateColor } from '../../../../../theme/colors'
import { CommonActions, RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { LogisticsRoutes, RootParams } from '../../../../../navigator/types'
import { PhotoTypes } from '../../../../../context/Logistics/fuel/types'
import { useTranslation } from 'react-i18next'
import { DOBRA } from '../../../../../theme/fonts'

const ROUTE_PHOTO: Record<PhotoTypes, keyof LogisticsRoutes> = {
  finalPhotoMeter: 'FinalPhotoMeter',
  finalPhotoRod: 'FinalPhotoRod',
  initialPhotoMeter: 'InitialPhotoMeter',
  initialPhotoRod: 'InitialPhotoRod',
  voucherPhoto: 'VoucherPhoto',
}

export const Summary = () => {
  const { t } = useTranslation()
  const { form, handleSaveRecord, setRecordByOrderNumber, handleResetForm } =
    useContext(FuelContext)

  const navigation = useNavigation<StackNavigationProp<RootParams, 'LogisticsStack'>>()
  const route = useRoute<RouteProp<LogisticsRoutes, 'Summary'>>()
  const [showSaveAlert, setShowSaveAlert] = useState(false)

  useEffect(() => {
    if (route.params?.orderNumber) {
      setRecordByOrderNumber(route.params.orderNumber)
    }
  }, [route.params])

  const isViewOnly = form.stateForm === 'pending' || form.stateForm === 'sent'
  const isActive = !isViewOnly

  const hasRequiredPhotos = !!(
    form.photos?.initialPhotoMeter?.uri &&
    form.photos?.finalPhotoMeter?.uri &&
    form.photos?.voucherPhoto?.uri
  )

  const getContinueRoute = (): keyof LogisticsRoutes => {
    const p = form.photos ?? {}
    if (!p.initialPhotoMeter && !form.testResult) return 'InitialPhotoRod'
    if (!p.initialPhotoMeter) return 'InitialPhotoMeter'
    if (!p.finalPhotoMeter) return 'FinalPhotoMeter'
    if (!p.voucherPhoto) return 'VoucherPhoto'
    return 'Summary'
  }

  const handleSave = () => {
    const time = new Date().toISOString().slice(0, 19)
    handleSaveRecord({
      ...form,
      stateForm: 'pending',
      statusDetail: {
        ...form.statusDetail,
        Recepcionado: { status: 'Recepcionado', time },
      },
    })
    setShowSaveAlert(true)
  }

  const handleConfirmSave = () => {
    setShowSaveAlert(false)
    handleResetForm()
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: 'HomeScreen' }],
      }),
    )
  }

  const handleOnPressCard = (values: { uri: string; photoType: PhotoTypes }) => {
    navigation.navigate('LogisticsStack', {
      screen: ROUTE_PHOTO[values.photoType],
      params: {
        returnToSummary: true,
        uri: values.uri,
      },
    })
  }

  const handleContinue = () => {
    navigation.navigate('LogisticsStack', { screen: getContinueRoute() })
  }

  const cardOnPress = isViewOnly ? undefined : handleOnPressCard

  return (
    <TemplatePage
      styleContent={{ padding: 0, marginBottom: 0 }}
      header
      title={t('Recepción combustible')}
    >
      <ScrollView style={[globalStyle.viewContainer, { padding: 0 }]}>
        <GrayHeader title={t('Resumen recepción combustible')} />
        <Header />
        <Separator customStyle={{ borderBottomWidth: 1 }} />

        {form.photos?.initialPhotoRod?.uri && (
          <Card
            date={+(form.photos?.initialPhotoRod?.dateTime ?? 0)}
            photoType={form.photos?.initialPhotoRod?.type ?? 'initialPhotoRod'}
            title={t('Varilla inicial')}
            uri={form.photos?.initialPhotoRod?.uri ?? ''}
            onPress={cardOnPress}
          />
        )}

        {form.photos?.finalPhotoRod?.uri && (
          <Card
            hasBg
            date={+(form.photos?.finalPhotoRod?.dateTime ?? 0)}
            title={t('Varilla final')}
            uri={form.photos?.finalPhotoRod?.uri ?? ''}
            label={`${t('Prueba de agua ')}${form.testResult}`}
            photoType={form.photos?.finalPhotoRod?.type ?? 'finalPhotoRod'}
            onPress={cardOnPress}
          />
        )}

        <Card
          date={+(form.photos?.initialPhotoMeter?.dateTime ?? 0)}
          title={t('Medidor inicial')}
          uri={form.photos?.initialPhotoMeter?.uri ?? ''}
          photoType={form.photos?.initialPhotoMeter?.type ?? 'initialPhotoMeter'}
          onPress={cardOnPress}
        />
        <Card
          hasBg
          date={+(form.photos?.finalPhotoMeter?.dateTime ?? 0)}
          title={t('Medidor final')}
          uri={form.photos?.finalPhotoMeter?.uri ?? ''}
          label={`${t('Registro')} ${form.meterNumber}`}
          photoType={form.photos?.finalPhotoMeter?.type ?? 'finalPhotoMeter'}
          onPress={cardOnPress}
        />
        <Card
          date={+(form.photos?.voucherPhoto?.dateTime ?? 0)}
          title={t('Comprobante de entrega')}
          uri={form.photos?.voucherPhoto?.uri ?? ''}
          label={`${t('Registro')} ${form.voucherNumber}`}
          photoType='voucherPhoto'
          onPress={cardOnPress}
        />

        <View style={{ padding: 24, alignItems: 'center', gap: 12 }}>
          {isActive && hasRequiredPhotos && (
            <Button
              label={t('Guardar recepción')}
              onPress={handleSave}
              customStyle={{ width: 200, height: 48, padding: 4, justifyContent: 'center' }}
            />
          )}
          {isActive && !hasRequiredPhotos && (
            <Button
              label={t('Continuar registro')}
              onPress={handleContinue}
              customStyle={{ width: 200, height: 48, padding: 4, justifyContent: 'center' }}
            />
          )}
        </View>
      </ScrollView>

      {showSaveAlert && (
        <AlertMessage action={handleConfirmSave}>
          <View style={{ alignItems: 'center', gap: 8, padding: 24 }}>
            <SuccessIcon height={40} width={40} />
            <Text style={style.title}>{t('Registro guardado')}</Text>
            <Text style={style.indications}>
              {t('Se enviará automáticamente cuando haya conexión a internet.')}
            </Text>
            <Text style={style.indications}>
              {t('Puedes ver el estado en Registros Combustible.')}
            </Text>
          </View>
        </AlertMessage>
      )}
    </TemplatePage>
  )
}

const style = StyleSheet.create({
  title: {
    color: corporateColor,
    fontFamily: DOBRA,
    fontSize: 18,
    fontWeight: '500',
    lineHeight: 22,
    marginTop: 8,
  },
  indications: {
    color: corporateColor,
    fontFamily: DOBRA,
    fontSize: 15,
    fontWeight: '400',
    lineHeight: 20,
    marginTop: 4,
    textAlign: 'center',
  },
})
